import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Switch,
	TextField,
	Typography,
} from "@mui/material"
import { Field, Form, Formik } from "formik"
import { useConfirm } from "material-ui-confirm"
import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import * as Yup from "yup"

import LoadingButton from "../../../../../../../../design/wilya-loading-button"
import { SHIFT_PURPOSE_DETAIL_DEFAULT_MAX_LENGTH } from "../../../../../../../../utils/shift-purpose-detail-field"
import useGatAdminConfigData from "../../../../../../controllers/use-gat-admin-config-data"
import {
	createShiftPurpose,
	editShiftPurpose,
	updateShiftPurposeStatus,
} from "../../../../../../store/gat-admin-slice"

export interface ShiftPurposeFormValues {
	name: string
	id?: number
	isDetailFieldEnabled: boolean
	detailFieldLabel: string
	detailFieldMaxLength?: number
}

interface ShiftPurposeFormProps {
	open: boolean
	onClose: () => void
	mode: "add" | "edit"
	initialData: ShiftPurposeFormValues | null
}

// The question is shown as the one-line label of the answer field when
// posting a Shift, and about 100 characters fit at the narrowest dialog width.
// Stricter than GATConst.ShiftPurposeDetailFieldLabelMaxLength until the
// server is lowered to match.
const DETAIL_FIELD_LABEL_MAX_LENGTH = 100

const validationSchema = Yup.object().shape({
	name: Yup.string()
		.required("Name is required")
		.max(100, "Name must be 100 characters or less"),
	isDetailFieldEnabled: Yup.boolean(),
	detailFieldLabel: Yup.string().when("isDetailFieldEnabled", {
		is: true,
		then: Yup.string()
			.required("Field label is required")
			.max(
				DETAIL_FIELD_LABEL_MAX_LENGTH,
				`Field label must be ${DETAIL_FIELD_LABEL_MAX_LENGTH} characters or less`
			),
		otherwise: Yup.string(),
	}),
})

function ShiftPurposeForm({
	open,
	onClose,
	mode = "add",
	initialData = null,
}: ShiftPurposeFormProps) {
	const dispatch = useDispatch()
	const confirm = useConfirm()
	const {
		createShiftPurposeStatus,
		editShiftPurposeStatus,
		updateShiftPurposeStatusStatus,
		companyDataValue,
		shiftPurposesData,
	} = useGatAdminConfigData()

	const initialValues: ShiftPurposeFormValues = {
		name: "",
		isDetailFieldEnabled: false,
		detailFieldLabel: "",
		...initialData,
	}

	const handleSubmit = (values: ShiftPurposeFormValues) => {
		const name = values.name.trim()
		const payload = {
			name,
			companyId: companyDataValue.id,
			detailField: values.isDetailFieldEnabled
				? {
						label: values.detailFieldLabel.trim(),
						// isRequired and maxLength are server-owned and ignored on
						// write; sent only to keep the stored purpose consistent
						// until the next fetch.
						isRequired: true,
						maxLength:
							values.detailFieldMaxLength ??
							SHIFT_PURPOSE_DETAIL_DEFAULT_MAX_LENGTH,
					}
				: null,
		}
		if (mode === "add") {
			const inactiveMatch = shiftPurposesData.find(
				(sp) =>
					sp.name.toLowerCase() === name.toLowerCase() && !sp.isActive
			)
			if (inactiveMatch) {
				confirm({
					title: "Reactivate Shift Purpose",
					content: `A shift purpose named "${inactiveMatch.name}" already exists but is inactive. Do you want to reactivate it instead of creating a new one?`,
					cancellationText: "Cancel",
					confirmationText: "Reactivate",
				})
					.then(() => {
						dispatch(
							updateShiftPurposeStatus({
								id: inactiveMatch.id,
								isActive: true,
							})
						)
						onClose()
					})
					.catch(() => undefined)
				return
			}
			dispatch(createShiftPurpose(payload))
		} else {
			dispatch(editShiftPurpose({ ...payload, id: initialData!.id! }))
		}
	}

	useEffect(() => {
		if (
			createShiftPurposeStatus === "fulfilled" ||
			editShiftPurposeStatus === "fulfilled" ||
			updateShiftPurposeStatusStatus === "fulfilled"
		) {
			onClose()
		}
	}, [
		createShiftPurposeStatus,
		editShiftPurposeStatus,
		updateShiftPurposeStatusStatus,
		onClose,
	])

	const isEditMode = mode === "edit"

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
			<DialogTitle sx={{ padding: "32px 32px 20px 32px" }}>
				{isEditMode ? "Edit Shift Purpose" : "Add Shift Purpose"}
			</DialogTitle>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ errors, touched, dirty, values, setFieldValue }) => {
					// A question saved before the limit was lowered can already be
					// too long. Show that straight away: otherwise editing only the
					// name disables Update with no visible reason.
					const isDetailFieldLabelTooLong =
						values.detailFieldLabel.length >
						DETAIL_FIELD_LABEL_MAX_LENGTH
					const isDetailFieldLabelErrorShown = !!(
						errors.detailFieldLabel &&
						(touched.detailFieldLabel || isDetailFieldLabelTooLong)
					)
					return (
					<Form>
						<DialogContent sx={{ padding: "20px 32px" }}>
							<Field
								as={TextField}
								fullWidth
								name='name'
								label='Name'
								variant='filled'
								error={!!(errors.name && touched.name)}
								helperText={touched.name && errors.name}
								inputProps={{ maxLength: 100 }}
							/>
							<Box sx={{ marginTop: "24px" }}>
								<FormControlLabel
									control={
										<Switch
											id='toggle-shift-purpose-detail-field'
											checked={values.isDetailFieldEnabled}
											onChange={(e) =>
												// Only the toggle changes here. Clearing
												// detailFieldLabel in the same handler would
												// validate against the stale toggle value and
												// leave a phantom "required" error that keeps
												// the submit button disabled; the label is
												// dropped in handleSubmit instead.
												setFieldValue(
													"isDetailFieldEnabled",
													e.target.checked
												)
											}
										/>
									}
									label='Ask for additional details'
								/>
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontWeight: 400,
										fontSize: "12px",
										color: "rgba(0, 0, 0, 0.6)",
									}}
								>
									Supervisors must fill in this free-text field when
									they select this purpose while posting a Shift.
								</Typography>
							</Box>
							{values.isDetailFieldEnabled ? (
								<Box sx={{ marginTop: "16px" }}>
									<Field
										as={TextField}
										fullWidth
										name='detailFieldLabel'
										label='What should supervisors enter?'
										variant='filled'
										error={isDetailFieldLabelErrorShown}
										helperText={
											<Box
												component='span'
												sx={{
													display: "flex",
													justifyContent: "space-between",
													gap: 2,
												}}
											>
												<span>
													{(isDetailFieldLabelErrorShown &&
														errors.detailFieldLabel) ||
														"Becomes the label of the field supervisors fill in, for example \"Client name\""}
												</span>
												<Box component='span' sx={{ flexShrink: 0 }}>
													{`${values.detailFieldLabel.length} / ${DETAIL_FIELD_LABEL_MAX_LENGTH}`}
												</Box>
											</Box>
										}
										inputProps={{
											maxLength: DETAIL_FIELD_LABEL_MAX_LENGTH,
										}}
									/>
								</Box>
							) : null}
						</DialogContent>
						<DialogActions sx={{ padding: "25px" }}>
							<Button onClick={onClose}>Cancel</Button>
							<LoadingButton
								type='submit'
								variant='contained'
								color='primary'
								disabled={Object.keys(errors).length > 0 || !dirty}
								loading={
									createShiftPurposeStatus === "pending" ||
									editShiftPurposeStatus === "pending"
								}
							>
								{isEditMode ? "Update" : "Add"}
							</LoadingButton>
						</DialogActions>
					</Form>
					)
				}}
			</Formik>
		</Dialog>
	)
}

export default ShiftPurposeForm
